import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Plus, Rocket, Ban, Trash2, Users, CalendarClock, Loader2, ChevronDown, ChevronUp, Save, Send, ShieldCheck, Eye, MousePointerClick, TrendingUp,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  launched: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};
const STAGE_STYLES = {
  pending: 'bg-slate-100 text-slate-600',
  queued: 'bg-blue-100 text-blue-800',
  rolling: 'bg-teal-100 text-teal-800',
  scheduled: 'bg-amber-100 text-amber-800',
  sending: 'bg-blue-100 text-blue-800',
  sent: 'bg-emerald-100 text-emerald-800',
  draft: 'bg-slate-100 text-slate-600',
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : ' - ');

export default function WebinarAutomations({ api }) {
  const BASE = `${api}/admin/webinar-automations`;
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null); // full automation for the expanded card
  const [form, setForm] = useState({
    audience_type: 'suppliers', webinar_at: '', duration_minutes: 60,
    webinar_title: '', reg_url: '', name: '', webinar_slug: '',
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(BASE);
      setItems(data.automations || []);
    } catch { toast.error('Failed to load automations'); }
  }, [BASE]);

  useEffect(() => { load(); }, [load]);

  // poll live progress while any automation is launched
  useEffect(() => {
    if (!items.some((a) => a.status === 'launched')) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [items, load]);

  // load full detail (per-stage open/click stats) whenever a card is expanded, and refresh it
  useEffect(() => {
    if (!expanded) { setDetail(null); return undefined; }
    let alive = true;
    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`${BASE}/${expanded}`);
        if (alive) setDetail(data);
      } catch { /* ignore */ }
    };
    fetchDetail();
    const t = setInterval(fetchDetail, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [expanded, BASE]);

  const create = async () => {
    if (!form.webinar_at) return toast.error('Pick the webinar date and time');
    setBusy(true);
    try {
      await axios.post(BASE, {
        audience_type: form.audience_type,
        webinar_at: form.webinar_at,
        timezone: 'Europe/London',
        duration_minutes: Number(form.duration_minutes) || 60,
        webinar_title: form.webinar_title || null,
        reg_url: form.reg_url || null,
        name: form.name || null,
        webinar_slug: form.webinar_slug || null,
      });
      toast.success('Automation created as draft');
      setShowNew(false);
      setForm({ audience_type: 'suppliers', webinar_at: '', duration_minutes: 60, webinar_title: '', reg_url: '', name: '', webinar_slug: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create automation'); }
    finally { setBusy(false); }
  };

  const launch = async (a) => {
    if (!window.confirm(
      `Launch "${a.name}"?\n\nThis will:\n• start contact discovery + email enrichment for ${a.audience_type === 'suppliers' ? 'tech suppliers' : 'care providers'}\n• send Email 1 now to ~${a.recipient_count} recipients, and keep sending it to anyone newly discovered until 5 days before the webinar (LIVE)\n• send Email 2 to everyone 5 days before the webinar\n• send Email 3 the day before the webinar\n\nAnyone who has already registered for this webinar is automatically excluded, and stays excluded from later emails if they register later.\n\nThis sends real emails. Continue?`)) return;
    setBusy(true);
    try {
      await axios.post(`${BASE}/${a.id}/launch`);
      toast.success('Automation launched - Email 1 is sending, Emails 2 & 3 scheduled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Launch failed'); }
    finally { setBusy(false); }
  };

  const cancel = async (a) => {
    if (!window.confirm(`Cancel "${a.name}"? Any not-yet-sent emails will be unscheduled.`)) return;
    setBusy(true);
    try { await axios.post(`${BASE}/${a.id}/cancel`); toast.success('Automation cancelled'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This removes the automation and unschedules pending emails.`)) return;
    setBusy(true);
    try { await axios.delete(`${BASE}/${a.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="webinar-automations">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#00A79D]/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-4.5 h-4.5 text-[#00A79D]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#0B2942]">Webinar automations</h3>
            <p className="text-xs text-slate-500 max-w-xl">Pick an audience and a webinar date. On launch it enriches contacts and sends Email 1 to everyone found (and anyone discovered later, until 5 days before), then Email 2 five days before the webinar and Email 3 the day before.</p>
          </div>
        </div>
        <Button onClick={() => setShowNew((s) => !s)} className="bg-[#00A79D] hover:bg-[#00938a] text-white" data-testid="automation-new-btn">
          <Plus className="w-4 h-4 mr-1" />New automation
        </Button>
      </div>

      {showNew && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="automation-new-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Audience</label>
              <select value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="automation-audience">
                <option value="suppliers">Tech suppliers</option>
                <option value="care_providers">Care providers</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Webinar date &amp; time (UK)</label>
              <input type="datetime-local" value={form.webinar_at} onChange={(e) => setForm({ ...form, webinar_at: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="automation-webinar-at" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Duration (mins)</label>
              <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="automation-duration" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Webinar title (optional)</label>
              <Input value={form.webinar_title} onChange={(e) => setForm({ ...form, webinar_title: e.target.value })} placeholder="Defaults to a sensible title" data-testid="automation-title" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Registration URL (optional)</label>
              <Input value={form.reg_url} onChange={(e) => setForm({ ...form, reg_url: e.target.value })} placeholder="/webinars" data-testid="automation-regurl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Registrant list to exclude (optional)</label>
              <Input value={form.webinar_slug} onChange={(e) => setForm({ ...form, webinar_slug: e.target.value })} placeholder={form.audience_type === 'suppliers' ? 'suppliers' : 'providers'} data-testid="automation-slug" />
              <p className="text-[11px] text-slate-400 mt-1">People who registered for this webinar are auto-excluded from every email.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Automation name (optional)</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Auto-named from audience + date" data-testid="automation-name" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowNew(false)} data-testid="automation-cancel-new">Cancel</Button>
            <Button onClick={create} disabled={busy} className="bg-[#00A79D] hover:bg-[#00938a] text-white" data-testid="automation-create-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Create draft
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl p-8 text-center" data-testid="automation-empty">
            No automations yet. Create one to enrich an audience and drip your 3 webinar emails automatically.
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`automation-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#0B2942] text-sm">{a.name}</span>
                  <Badge className={`${STATUS_STYLES[a.status] || 'bg-slate-100'} border-0 text-[11px]`} data-testid={`automation-status-${a.id}`}>{a.status}</Badge>
                  <Badge className="bg-[#00A79D]/10 text-[#00857D] border-0 text-[11px]">{a.audience_type === 'suppliers' ? 'Tech suppliers' : 'Care providers'}</Badge>
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />Webinar {fmtDT(a.webinar_at)}</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />~{a.recipient_count ?? 0} enriched recipients now</span>
                  {a.excluded_registrants > 0 && (
                    <span className="flex items-center gap-1 text-emerald-600" data-testid={`automation-excluded-${a.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5" />{a.excluded_registrants} registrant{a.excluded_registrants === 1 ? '' : 's'} auto-excluded
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {a.status === 'draft' && (
                  <Button size="sm" onClick={() => launch(a)} disabled={busy} className="bg-[#00A79D] hover:bg-[#00938a] text-white" data-testid={`automation-launch-${a.id}`}>
                    <Rocket className="w-4 h-4 mr-1" />Launch
                  </Button>
                )}
                {a.status === 'launched' && (
                  <Button size="sm" variant="outline" onClick={() => cancel(a)} disabled={busy} className="text-amber-700 border-amber-300" data-testid={`automation-cancel-${a.id}`}>
                    <Ban className="w-4 h-4 mr-1" />Cancel
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(a)} className="text-slate-400 hover:text-red-600" data-testid={`automation-delete-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === a.id ? null : a.id)} data-testid={`automation-expand-${a.id}`}>
                  {expanded === a.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {a.enrich && a.status === 'launched' && (
              <div className="mt-3 text-xs bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-slate-600 flex items-center gap-2" data-testid={`automation-enrich-${a.id}`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00A79D]" />
                Contact discovery + enrichment running in the background - recipient list grows over time.
              </div>
            )}

            {expanded === a.id && (
              <div className="mt-4 space-y-2 border-t border-slate-100 pt-3" data-testid={`automation-stages-${a.id}`}>
                {((detail && detail.id === a.id ? detail.stages : a.stages) || []).map((s) => (
                  <div key={s.stage} className="flex items-start justify-between gap-3 text-sm py-2 border-b border-slate-50 last:border-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#0B2942]">{s.label}</span>
                        <Badge className={`${STAGE_STYLES[s.status] || 'bg-slate-100'} border-0 text-[10px]`}>{s.status}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{s.subject}</p>
                      {s.stage === 1 && s.timeline && s.timeline.total > 0 && (
                        <div className="mt-2" data-testid={`automation-e1-timeline-${a.id}`}>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500">
                            <TrendingUp className="w-3 h-3 text-[#00A79D]" />
                            <span className="font-semibold text-[#0B2942]">{s.timeline.total}</span> sent over time
                            {s.timeline.last_sent_at && <span className="text-slate-400">· last {fmtDT(s.timeline.last_sent_at)}</span>}
                          </div>
                          {s.timeline.days.length > 0 && (
                            <div className="flex items-end gap-1 mt-1.5 h-10">
                              {(() => {
                                const max = Math.max(...s.timeline.days.map((d) => d.count), 1);
                                return s.timeline.days.slice(-14).map((d) => (
                                  <div key={d.day} className="flex flex-col items-center gap-1" title={`${d.day}: ${d.count}`}>
                                    <div className="w-3 rounded-sm bg-[#00A79D]/70" style={{ height: `${Math.max(4, Math.round((d.count / max) * 32))}px` }} />
                                    <span className="text-[9px] text-slate-400">{d.day.slice(5)}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs text-slate-400 shrink-0">
                      {s.scheduled_at && <div className="flex items-center gap-1 justify-end"><Send className="w-3 h-3" />{fmtDT(s.scheduled_at)}</div>}
                      {typeof s.sent_count === 'number' && s.total_recipients != null && (
                        <div className="text-slate-500">{s.sent_count}/{s.total_recipients} sent</div>
                      )}
                      {s.stats && s.stats.sent > 0 && (
                        <div className="flex items-center gap-3 justify-end mt-1" data-testid={`automation-stage-stats-${s.stage}`}>
                          <span className="flex items-center gap-1 text-[#00857D]"><Eye className="w-3 h-3" />{s.stats.open_rate}% <span className="text-slate-400">({s.stats.opens_unique})</span></span>
                          <span className="flex items-center gap-1 text-amber-600"><MousePointerClick className="w-3 h-3" />{s.stats.click_rate}% <span className="text-slate-400">({s.stats.clicks_unique})</span></span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
